import {
  AlertCircle,
  CheckCircle2,
  CreditCard,
  Download,
  ExternalLink,
  FileText,
  Globe,
  Loader2,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useAction, useMutation } from "convex/react";
import { useEffect, useState } from "react";

import { APPROVED_STRIPE_CONNECT_COUNTRIES } from "@/constants/approvedCountries";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Elements } from "@stripe/react-stripe-js";
import { Id } from "@cvx/_generated/dataModel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { STRIPE_EVENTS } from "@/constants/analyticsEvents";
import Stripe from "stripe";
import StripeConnectElements from "@/components/Settings/StripeConnectElements";
import StripePaymentElement from "@/components/Settings/StripePaymentElement";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { countries } from "countries-list";
import getUnicodeFlagIcon from "country-flag-icons/unicode";
import { loadStripe } from "@stripe/stripe-js";
import { useAccount } from "@/state/useAccount";
import { useMixpanel } from "@/hooks/useMixpanel";
import { useQuery } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";

// Stripe Connect supported countries based on official documentation
// https://docs.stripe.com/connect/cross-border-payouts
const STRIPE_CONNECT_COUNTRIES = [
  "AU", // Australia
  "AT", // Austria
  "BE", // Belgium
  "BR", // Brazil
  "BG", // Bulgaria
  "CA", // Canada
  "CI", // Côte d'Ivoire
  "HR", // Croatia
  "CY", // Cyprus
  "CZ", // Czech Republic
  "DK", // Denmark
  "EE", // Estonia
  "FI", // Finland
  "FR", // France
  "DE", // Germany
  "GH", // Ghana
  "GI", // Gibraltar
  "GR", // Greece
  "HK", // Hong Kong
  "HU", // Hungary
  "IN", // India
  "ID", // Indonesia
  "IE", // Ireland
  "IT", // Italy
  "JP", // Japan
  "KE", // Kenya
  "LV", // Latvia
  "LI", // Liechtenstein
  "LT", // Lithuania
  "LU", // Luxembourg
  "MY", // Malaysia
  "MT", // Malta
  "MX", // Mexico
  "NL", // Netherlands
  "NZ", // New Zealand
  "NG", // Nigeria
  "NO", // Norway
  "PL", // Poland
  "PT", // Portugal
  "RO", // Romania
  "SG", // Singapore
  "SK", // Slovakia
  "SI", // Slovenia
  "ZA", // South Africa
  "ES", // Spain
  "SE", // Sweden
  "CH", // Switzerland
  "TH", // Thailand
  "AE", // United Arab Emirates
  "GB", // United Kingdom
  "US", // United States
];

export const Route = createFileRoute(
  "/_app/_auth/dashboard/_layout/settings/billing",
)({
  component: BillingSettings,
  beforeLoad: () => ({
    title: "Billing",
    headerTitle: "Billing",
    headerDescription: "Manage payment methods and billing history.",
  }),
});

const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY);

export default function BillingSettings() {
  const { data: user } = useQuery(convexQuery(api.app.getCurrentUser, {}));
  const { data: bankDetails } = useQuery(
    convexQuery(api.app.getUserBankDetails, {}),
  );
  const { data: invoicesAndPayouts } = useQuery(
    convexQuery(api.app.getCreatorInvoicesAndPayouts, {}),
  );

  // Get contract and campaign information for creators
  const { creatorId, contractId } = useAccount();
  const { data: partnership } = useQuery({
    ...convexQuery(api.partnerships.getPartnership, {
      partnershipId: contractId as Id<"creatorContracts">,
    }),
    enabled: !!contractId && !!creatorId,
  });

  // Check if this is the special campaign that allows all countries and manual transfer
  const SPECIAL_CAMPAIGN_ID = "nd7f46a8fdvef2b71s7m4tpgkn7kr5cg";
  const isSpecialCampaign = partnership?.campaign._id === SPECIAL_CAMPAIGN_ID;

  // Determine which countries to show based on campaign
  const countriesToShow = isSpecialCampaign
    ? STRIPE_CONNECT_COUNTRIES
    : APPROVED_STRIPE_CONNECT_COUNTRIES;

  // Convert countries object to array, filter for appropriate countries, and sort by name
  const stripeCountryOptions = Object.entries(countries)
    .filter(([code]) => countriesToShow.includes(code))
    .map(([code, country]) => ({
      code,
      name: country.name,
      flag: getUnicodeFlagIcon(code),
    }))
    .sort((a, b) => a.name.localeCompare(b.name));

  const createStripeCustomer = useAction(api.stripe.createStripeCustomer);
  const getCustomerSessionClientSecret = useAction(
    api.stripe.getCustomerSessionClientSecret,
  );
  const checkUserStripeConnectAccount = useAction(
    api.stripe.checkUserStripeConnectAccount,
  );
  const createStripeConnectAccount = useAction(
    api.stripe.createStripeConnectAccount,
  );
  const updateUserBankDetails = useMutation(api.app.updateUserBankDetails);

  const { toast } = useToast();
  const navigate = useNavigate();
  const { track } = useMixpanel();

  const [clientSecret, setClientSecret] = useState<string | undefined>(
    undefined,
  );
  const [connectAccount, setConnectAccount] = useState<Stripe.Account | null>(
    null,
  );
  const [connectAccountLoading, setConnectAccountLoading] = useState(false);
  const [connectAccountError, setConnectAccountError] = useState<string | null>(
    null,
  );
  const [creatingAccount, setCreatingAccount] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState<string>("");

  // Bank details form state
  const [showBankDetailsModal, setShowBankDetailsModal] = useState(false);
  const [showBankDetailsForm, setShowBankDetailsForm] = useState(false);
  const [savingBankDetails, setSavingBankDetails] = useState(false);
  const [bankDetailsForm, setBankDetailsForm] = useState({
    firstName: "",
    lastName: "",
    homeAddress: "",
    iban: "",
    accountNumber: "",
    bicOrSwift: "",
  });

  const isCreator = !!creatorId;

  // Populate form when bank details are loaded
  useEffect(() => {
    if (bankDetails) {
      setBankDetailsForm({
        ...bankDetails,
        iban: bankDetails.iban || "",
      });
    }
  }, [bankDetails]);

  // Ensure Stripe customer is created when component loads
  useEffect(() => {
    if (user?._id && !isCreator) {
      createStripeCustomer({})
        .then(() => {
          console.log("Stripe customer ensured");
        })
        .catch((error) => {
          console.warn("Could not create Stripe customer:", error);
        });
    }
  }, [user?._id, isCreator, createStripeCustomer]);

  // Load regular payment methods for non-creators
  useEffect(() => {
    if (user?._id && !isCreator) {
      console.log("Loading customer session client secret");
      getCustomerSessionClientSecret({ userId: user._id }).then((secret) => {
        setClientSecret(secret);
      });
    }
  }, [user?._id, isCreator]);

  // Load Stripe Connect account for creators
  useEffect(() => {
    if (isCreator) {
      setConnectAccountLoading(true);
      setConnectAccountError(null);

      checkUserStripeConnectAccount({})
        .then((result) => {
          if (result.hasAccount && result.account) {
            setConnectAccount(result.account);
          } else {
            setConnectAccount(null);
            setConnectAccountError("No Stripe Connect account found");
          }
        })
        .catch((error) => {
          console.error("Error checking Stripe Connect account:", error);
          const errorMessage =
            error.message || "Failed to check payment account";
          setConnectAccountError(errorMessage);

          // Only show toast for actual errors, not for missing accounts
          toast({
            title: "Error",
            description:
              "Failed to load payment account information. Please try again.",
            variant: "destructive",
          });
        })
        .finally(() => {
          setConnectAccountLoading(false);
        });
    }
  }, [isCreator, toast]);

  const handleCreateConnectAccount = async () => {
    if (!selectedCountry) {
      toast({
        title: "Country Required",
        description:
          "Please select your country before setting up your payment account.",
        variant: "destructive",
      });
      return;
    }

    setCreatingAccount(true);

    // Track initiation
    track(STRIPE_EVENTS.CONNECT_ACCOUNT_CREATION_INITIATED, {
      user_id: user?._id,
      creator_id: user?.creator?._id,
      country: selectedCountry,
      timestamp: new Date().toISOString(),
    });

    try {
      const accountLinkUrl = await createStripeConnectAccount({
        country: selectedCountry,
      });

      toast({
        title: "Redirecting to Stripe",
        description:
          "You'll be redirected to Stripe to set up your payment account.",
      });

      // Track successful creation (before redirect)
      track(STRIPE_EVENTS.CONNECT_ACCOUNT_ONBOARDING_STARTED, {
        user_id: user?._id,
        creator_id: user?.creator?._id,
        country: selectedCountry,
        timestamp: new Date().toISOString(),
      });

      // Redirect to Stripe onboarding
      window.location.href = accountLinkUrl;
    } catch (error) {
      console.error("Error creating Stripe Connect account:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to create account";
      setConnectAccountError(errorMessage);

      // Track failure
      track(STRIPE_EVENTS.CONNECT_ACCOUNT_CREATION_FAILED, {
        user_id: user?._id,
        creator_id: user?.creator?._id,
        country: selectedCountry,
        error_message: errorMessage,
        timestamp: new Date().toISOString(),
      });

      toast({
        title: "Error",
        description: errorMessage.includes("already exists")
          ? "You already have a payment account. Please refresh the page."
          : "Failed to create payment account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCreatingAccount(false);
    }
  };

  const handleBankDetailsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (
      !bankDetailsForm.firstName.trim() ||
      !bankDetailsForm.lastName.trim() ||
      !bankDetailsForm.homeAddress.trim() ||
      !bankDetailsForm.accountNumber.trim() ||
      !bankDetailsForm.bicOrSwift.trim()
    ) {
      toast({
        title: "Missing Fields",
        description: "Please fill in all required fields.",
        variant: "destructive",
      });
      return;
    }

    setSavingBankDetails(true);
    try {
      await updateUserBankDetails(bankDetailsForm);

      toast({
        title: "Success",
        description: bankDetails
          ? "Bank details updated successfully!"
          : "Bank details saved successfully!",
      });

      setShowBankDetailsForm(false);
      setShowBankDetailsModal(false);
    } catch (error) {
      console.error("Error saving bank details:", error);
      toast({
        title: "Error",
        description: "Failed to save bank details. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingBankDetails(false);
    }
  };

  const isConnectAccountComplete =
    connectAccount &&
    connectAccount.details_submitted &&
    connectAccount.charges_enabled;

  // Manual transfer option component to avoid duplication
  const manualTransferOption = isSpecialCampaign ? (
    <>
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">Or</span>
        </div>
      </div>

      <Dialog
        open={showBankDetailsModal}
        onOpenChange={setShowBankDetailsModal}
      >
        <DialogTrigger asChild>
          <Button variant="outline" className="w-full">
            I don't see my country
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Bank Transfer Setup</DialogTitle>
            <DialogDescription>
              Setting up manual bank transfers may result in:
              <br />
              • Additional verification requirements
              <br />
              • Manual processing and potential delays
              <br />
              • Back-and-forth communication for payment verification
              <br />
              <strong>
                We highly recommend using Stripe Connect instead for faster,
                automated payments.
              </strong>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setShowBankDetailsModal(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                setShowBankDetailsModal(false);
                setShowBankDetailsForm(true);
              }}
            >
              Continue Anyway
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  ) : null;

  if (isCreator) {
    return (
      <div className="flex flex-col gap-6 w-full">
        {/* Only show Payment Account setup if user doesn't have bank details */}
        {!bankDetails && (
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-primary">
                Payment Account
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage your payment account to receive earnings from
                partnerships
              </p>
            </div>

            {connectAccountLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
                <span className="ml-2">Loading account information...</span>
              </div>
            ) : connectAccountError ? (
              <div className="flex flex-col items-center gap-6 py-8">
                <AlertCircle className="h-12 w-12 text-muted-foreground" />
                <div className="text-center max-w-md">
                  <h4 className="text-lg font-medium text-primary mb-2">
                    Set Up Your Payment Account
                  </h4>
                  <p className="text-sm text-muted-foreground mb-6">
                    To receive earnings from your partnerships, you need to set
                    up a payment account. This will allow us to securely
                    transfer your earnings to your bank account.
                  </p>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="country"
                        className="flex items-center gap-2"
                      >
                        <Globe className="h-4 w-4" />
                        Where are you located or where is your business
                        incorporated?
                      </Label>
                      <Select
                        value={selectedCountry}
                        onValueChange={setSelectedCountry}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your country/region" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {stripeCountryOptions.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              <span className="flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={handleCreateConnectAccount}
                      disabled={creatingAccount || !selectedCountry}
                      className="flex items-center gap-2 w-full"
                    >
                      {creatingAccount ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <CreditCard className="h-4 w-4" />
                      )}
                      Set Up Payment Account
                    </Button>

                    {manualTransferOption}
                  </div>
                </div>
              </div>
            ) : isConnectAccountComplete ? null : (
              <div className="flex flex-col items-center gap-6 py-8">
                <AlertCircle className="h-12 w-12 text-yellow-500" />
                <div className="text-center max-w-md">
                  <h4 className="text-lg font-medium text-primary mb-2">
                    Complete Your Payment Setup
                  </h4>
                  <p className="text-sm text-muted-foreground mb-6">
                    Your payment account needs additional information to start
                    receiving payments.
                  </p>

                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label
                        htmlFor="country"
                        className="flex items-center gap-2"
                      >
                        <Globe className="h-4 w-4" />
                        Where are you located or where is your business
                        incorporated?
                      </Label>
                      <Select
                        value={selectedCountry}
                        onValueChange={setSelectedCountry}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select your country/region" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {stripeCountryOptions.map((country) => (
                            <SelectItem key={country.code} value={country.code}>
                              <span className="flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.name}</span>
                              </span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <Button
                      onClick={handleCreateConnectAccount}
                      disabled={creatingAccount || !selectedCountry}
                      className="flex items-center gap-2 w-full"
                    >
                      {creatingAccount ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )}
                      Complete Setup
                    </Button>

                    {manualTransferOption}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Add Connect Elements for complete accounts */}
        {isConnectAccountComplete && (
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-primary">
                Account Management
              </h3>
              <p className="text-sm text-muted-foreground">
                Manage your payment account, view notifications, payouts, and
                payment history
              </p>
            </div>

            <StripeConnectElements />
          </div>
        )}

        {/* Bank Details Form Modal */}
        <Dialog
          open={showBankDetailsForm}
          onOpenChange={setShowBankDetailsForm}
        >
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {bankDetails ? "Update Bank Details" : "Bank Details"}
              </DialogTitle>
              <DialogDescription>
                Enter your banking information for manual transfers. All
                sensitive data is encrypted.
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleBankDetailsSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name *</Label>
                  <Input
                    id="firstName"
                    value={bankDetailsForm.firstName}
                    onChange={(e) =>
                      setBankDetailsForm((prev) => ({
                        ...prev,
                        firstName: e.target.value,
                      }))
                    }
                    placeholder="Enter your first name"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name *</Label>
                  <Input
                    id="lastName"
                    value={bankDetailsForm.lastName}
                    onChange={(e) =>
                      setBankDetailsForm((prev) => ({
                        ...prev,
                        lastName: e.target.value,
                      }))
                    }
                    placeholder="Enter your last name"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="homeAddress">Home Address *</Label>
                <Textarea
                  id="homeAddress"
                  value={bankDetailsForm.homeAddress}
                  onChange={(e) =>
                    setBankDetailsForm((prev) => ({
                      ...prev,
                      homeAddress: e.target.value,
                    }))
                  }
                  placeholder="Enter your full address"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="iban">IBAN</Label>
                <Input
                  id="iban"
                  type="text"
                  value={bankDetailsForm.iban}
                  onChange={(e) =>
                    setBankDetailsForm((prev) => ({
                      ...prev,
                      iban: e.target.value,
                    }))
                  }
                  placeholder="Enter your IBAN (optional)"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="accountNumber">Account Number *</Label>
                <Input
                  id="accountNumber"
                  type="text"
                  value={bankDetailsForm.accountNumber}
                  onChange={(e) =>
                    setBankDetailsForm((prev) => ({
                      ...prev,
                      accountNumber: e.target.value,
                    }))
                  }
                  placeholder="Enter your account number"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="bicOrSwift">BIC/SWIFT Code *</Label>
                <Input
                  id="bicOrSwift"
                  type="text"
                  value={bankDetailsForm.bicOrSwift}
                  onChange={(e) =>
                    setBankDetailsForm((prev) => ({
                      ...prev,
                      bicOrSwift: e.target.value,
                    }))
                  }
                  placeholder="Enter your BIC/SWIFT code"
                  required
                />
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowBankDetailsForm(false)}
                  disabled={savingBankDetails}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={savingBankDetails}>
                  {savingBankDetails ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      {bankDetails ? "Update" : "Save"} Bank Details
                    </>
                  )}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Bank Details Display */}
        {bankDetails && (
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-6 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-primary">
                  Bank Details
                </h3>
                <p className="text-sm text-muted-foreground">
                  Your saved banking information for manual transfers
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBankDetailsForm(true)}
              >
                Update Details
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <Label className="text-muted-foreground">Name</Label>
                <p className="font-medium">
                  {bankDetails.firstName} {bankDetails.lastName}
                </p>
              </div>
              <div>
                <Label className="text-muted-foreground">Address</Label>
                <p className="font-medium">{bankDetails.homeAddress}</p>
              </div>
              {bankDetails.iban && (
                <div>
                  <Label className="text-muted-foreground">IBAN</Label>
                  <p className="font-mono">{bankDetails.iban}</p>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Account Number</Label>
                <p className="font-mono">{bankDetails.accountNumber}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">BIC/SWIFT</Label>
                <p className="font-mono">{bankDetails.bicOrSwift}</p>
              </div>
            </div>
          </div>
        )}

        {/* Invoices and Payouts Section */}
        {bankDetails && invoicesAndPayouts && (
          <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-primary">
                Invoices & Payouts
              </h3>
            </div>

            <div className="space-y-6">
              {/* Contract Payments */}
              {invoicesAndPayouts.contracts.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Contract Payments</h4>
                  <div className="space-y-3">
                    {invoicesAndPayouts.contracts.map((contract) => (
                      <Card
                        key={contract._id}
                        className="p-4 cursor-pointer hover:bg-accent/50 transition-colors"
                        onClick={() =>
                          navigate({ to: `/partnerships/${contract._id}` })
                        }
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-medium">
                              {contract.app?.name} - {contract.campaign?.name}
                            </p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(
                                contract.startDate,
                              ).toLocaleDateString()}{" "}
                              -{" "}
                              {new Date(contract.endDate).toLocaleDateString()}
                            </p>
                            {contract.paidAmount && (
                              <p className="text-sm font-medium text-green-600">
                                Paid: {contract.currency.toUpperCase()}{" "}
                                {contract.paidAmount}
                                {contract.paymentDate && (
                                  <span className="text-muted-foreground ml-2">
                                    on{" "}
                                    {new Date(
                                      contract.paymentDate,
                                    ).toLocaleDateString()}
                                  </span>
                                )}
                              </p>
                            )}
                          </div>
                          <div className="flex gap-2">
                            {contract.creatorInvoiceUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (contract.creatorInvoiceUrl) {
                                    window.open(
                                      contract.creatorInvoiceUrl,
                                      "_blank",
                                    );
                                  }
                                }}
                              >
                                <FileText className="h-4 w-4 mr-1" />
                                Invoice
                              </Button>
                            )}
                            {contract.paymentReceiptUrl && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (contract.paymentReceiptUrl) {
                                    window.open(
                                      contract.paymentReceiptUrl,
                                      "_blank",
                                    );
                                  }
                                }}
                              >
                                <Download className="h-4 w-4 mr-1" />
                                Receipt
                              </Button>
                            )}
                          </div>
                        </div>
                      </Card>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground mt-3 text-center">
                    Click on a contract to view detailed breakdown and
                    partnership information
                  </p>
                </div>
              )}

              {invoicesAndPayouts.contracts.length === 0 && (
                <div className="text-center py-8">
                  <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No invoices or payouts yet
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // Regular user (app founder) payment methods
  return (
    <div className="flex flex-col gap-6 w-full">
      <div className="rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-primary">
            Payment Methods
          </h3>
          <p className="text-sm text-muted-foreground">
            Add or update your payment information
          </p>
        </div>

        <Elements
          stripe={stripePromise}
          options={{
            mode: "setup",
            currency: "usd",
            customerSessionClientSecret: clientSecret,
            appearance: {
              theme: "night",
              variables: {
                spacingUnit: "4px",
                borderRadius: "4px",
                fontSizeBase: "14px",
              },
              rules: {
                ".Input": {
                  minWidth: "500px",
                },
              },
            },
          }}
        >
          <div className="w-full max-w-2xl">
            <StripePaymentElement />
          </div>
        </Elements>
      </div>
    </div>
  );
}
